import { Chip, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { cancelTempAssignmentEvent, fetchLatestTAEDEventByReferenceNumber } from "../../api/taed";
import { selectTempAssignmentData, updateSortModel } from "../../slices/tempAssignment/tempAssignmentSlice";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { generateExportCsvButtonText } from "../../util/commonUtils";
import {
  ACTIONS,
  ACTION_CANCEL,
  CLIENT,
  FEATURES,
  FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT,
  STATUS_ACTIVE,
  STATUS_CANCELED,
  TEMPORARY_ASSIGNMENT_PAGE_TYPE,
} from "../../util/constants";
import {
  convert_Timestamp_To_Luxon,
  convert_Timestamp_To_Luxon_no_local_time,
  getShiftDate,
} from "../../util/dateTimeFormatter";
import {
  CANCEL_TEMPORARY_ASSIGNMENT_TITLE,
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  REFERENCE_NUMBER_SUB_TITLE,
  TEMP_ASSIGNMENT_ACTION_ERROR_TITLE,
  TEMP_ASSIGNMENT_BASE_SCHEDULE_DONE_MESSAGE,
  TEMP_ASSIGNMENT_BASE_SCHEDULE_MISSING_MESSAGE,
  TEMP_ASSIGNMENT_CANCEL_INFO_MESSAGE,
  TEMP_ASSIGNMENT_CANCEL_MESSAGE,
  TEMP_ASSIGNMENT_CANCEL_WARNING_MESSAGE,
  TEMP_ASSIGNMENT_CONFIRMATION_TITLE,
  TEMP_ASSIGNMENT_SELF_ACTION_CANCEL_WARNING_MESSAGE,
  TEMP_ASSIGNMENT_SELF_ACTION_EDIT_WARNING_MESSAGE,
  YOU_CANNOT_EDIT_TEMPORARY_ASSIGNMENT_OVER_TWO_MONTHS_IN_PAST,
} from "../../util/messageConstants";
import {
  getSortedTempAssignmentsByBaseScheduleAdded,
  getSortedTempAssignmentsByLastUpdatedDate,
  getSortedTempAssignmentsByStatus,
  getTempEmployeeLastNameCustomComparator,
} from "../../util/taed/sortTempAssignments";
import { isBaseScheduleAdded } from "../../util/taedUtils";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay";
import CustomLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import ActionButton from "../fundamentals/ActionButton";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SuccessDialog from "../fundamentals/SuccessDialog";
import WarningDialog from "../fundamentals/WarningDialog";
import { TWO_MONTHS_IN_PAST } from "./../../util/dateHelper";
import TemporaryAssignmentEmployee from "./TemporaryAssignmentEmployee";
import TemporaryAssignmentFormToolbar from "./TemporaryAssignmentToolbar";

const useStyles = makeStyles((theme) => ({
  table: {
    height: `calc(85vh - 170px)`,
  },
  toolTip: {
    maxWidth: "150px",
    fontSize: "12px",
  },
  redStatusColor: {
    color: theme.palette.primary.swaRed,
  },
  baseScheduleMissingChip: {
    backgroundColor: theme.palette.primary.swaRed,
    color: theme.palette.secondary.swaWhite,
    width: "70px",
  },
  baseScheduleDoneChip: {
    backgroundColor: theme.palette.secondary.swaGreen,
    color: theme.palette.secondary.swaWhite,
    width: "70px",
  },
}));

const TemporaryAssignmentTable = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const tempAssignmentData = useSelector(selectTempAssignmentData);
  const history = useHistory();
  const [selectionModel, setSelectionModel] = useState([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isCancelWarningOpen, setCancelWarningOpen] = useState(false);
  const [isCancelSuccessDialogOpen, setIsCancelSuccessDialogOpen] = useState(false);
  const [isActiveTempAssignment, setIsActiveTempAssignment] = useState(false);
  const [cancelErrors, setCancelErrors] = useState("");
  const userSession = useSelector(selectUserSession);
  const [cancelEmpLastName, setCancelLastName] = useState("");
  const [cancelEmpFirstName, setCancelFirstName] = useState("");
  const [cancelEmployeeId, setCancelEmployeeId] = useState("");
  const [tempAssignmentActionData, setTempAssignmentActionData] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSelfEditWarningDialogOpen, setIsSelfEditWarningDialogOpen] = useState(false);
  const [isSelfCancelWarningDialogOpen, setIsSelfCancelWarningDialogOpen] = useState(false);

  const handleCancelWarningDialogClose = () => {
    setCancelWarningOpen(false);
    setIsActiveTempAssignment(false);
    setCommentsError(false);
    setCommentErrorMessage("");
    setIsLoading(false);
    setComments("");
  };
  const handleCancelYesButtonClicked = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT));
    } else {
      setIsLoading(true);
      cancelTempAssignment();
    }
  };
  const handleClickDialogClose = () => {
    setDialogOpen(false);
  };

  const renderBaseScheduleTime = (schedule, day) => {
    return !isBaseScheduleAdded(schedule)
      ? ""
      : day.isOFF
      ? "OFF"
      : day.isNA
      ? "N/A"
      : `${day.start.hr}:${day.start.min}-${day.end.hr}:${day.end.min}`;
  };

  const renderEmployeeCell = (tempAssignment) => {
    return (
      <TemporaryAssignmentEmployee
        tempAssignment={tempAssignment}
        handleEditClick={() => editTempAssignment(tempAssignment)}
      />
    );
  };
  const cancelTempAssignment = async () => {
    const payload = {
      referenceNumber: tempAssignmentActionData.id,
      modifiedByEid: userSession.eid,
      modifiedByFirstName: userSession.firstName,
      modifiedByLastName: userSession.lastName,
      modifiedByWorkgroup: getWorkgroupGivenJobCode(userSession.jobCode),
      comments: comments,
      createdBy: CLIENT,
    };
    await cancelTempAssignmentEvent(payload)
      .then((res) => res.json())
      .then(async (result) => {
        setCancelWarningOpen(false);
        setIsLoading(false);
        setCancelErrors("");
        setComments("");
        setIsCancelSuccessDialogOpen(true);
        setIsActiveTempAssignment(false);
      })
      .catch((err) => {
        console.log(err, "error");
        setCancelErrors("Error. Something went Wrong.");
      });
  };
  const editTempAssignment = (tempAssignment) => {
    if (tempAssignment.eid === userSession.eid) {
      setIsSelfEditWarningDialogOpen(true);
      return;
    }

    const tempAssignmentChildItems = props.featureConfig.childItems;
    const editTempAssignment = tempAssignmentChildItems.find(
      (item) => item.key === FEATURES.CREATE_TEMPORARY_ASSIGNMENT
    );

    if (tempAssignment.endDate.ts < TWO_MONTHS_IN_PAST(props.station).ts) {
      setDialogMessage(YOU_CANNOT_EDIT_TEMPORARY_ASSIGNMENT_OVER_TWO_MONTHS_IN_PAST);
      return setDialogOpen(true);
    } else {
      history.push({ pathname: editTempAssignment.path, state: tempAssignment });
    }
  };

  const handleCancelClick = async (employeeData) => {
    const latestTAEDEvent = await fetchLatestTAEDEventByReferenceNumber(employeeData.id);
    if (
      latestTAEDEvent.latestTempAssignmentEvent &&
      latestTAEDEvent.latestTempAssignmentEvent.status === STATUS_CANCELED
    ) {
      setDialogMessage("Invalid Action, try refreshing the page.");
      return setDialogOpen(true);
    }

    if (employeeData.eid === userSession.eid) {
      setIsSelfCancelWarningDialogOpen(true);
      return;
    }

    if (employeeData.status === STATUS_ACTIVE) {
      setIsActiveTempAssignment(true);
    }
    setCancelEmployeeId(employeeData.eid);
    setCancelFirstName(employeeData.empFirstName);
    setCancelLastName(employeeData.empLastName);
    setCancelWarningOpen(true);
    setTempAssignmentActionData(employeeData);
  };
  const renderActionsCell = (value) => {
    return props.station === value.tempStation && value.status !== STATUS_CANCELED ? (
      <Grid>
        <ActionButton variant="contained" color="primary" onClick={() => editTempAssignment(value)}>
          Edit
        </ActionButton>
        <br />
        {value.endDate.ts > TWO_MONTHS_IN_PAST(props.station).ts ? (
          <ActionButton variant="contained" color="primary" onClick={() => handleCancelClick(value)}>
            {ACTION_CANCEL}
          </ActionButton>
        ) : (
          ""
        )}
      </Grid>
    ) : (
      ""
    );
  };

  const renderDesktopTempAssignments = () => {
    const columns = [
      {
        field: "lastUpdatedDate",
        headerName: "Last Updated",
        type: "dateTime",
        flex: 0.24,
        valueFormatter: (params) => {
          return params.value.toFormat("LL/dd/yyyy T ZZZZ");
        },
        sortComparator: getSortedTempAssignmentsByLastUpdatedDate,
      },
      {
        field: "employee",
        headerName: "Employee",
        type: "string",
        flex: 0.3,
        renderCell: (params) => {
          return renderEmployeeCell(params.row);
        },
        sortComparator: getTempEmployeeLastNameCustomComparator,
      },
      {
        field: "workgroup",
        headerName: "Workgroup",
        type: "string",
        flex: 0.16,
      },

      {
        field: "homeStation",
        headerName: "Home Station",
        type: "string",
        flex: 0.15,
      },
      {
        field: "tempStation",
        headerName: "Temp Station",
        type: "string",
        flex: 0.15,
      },
      {
        field: "startDate",
        headerName: "Start Date",
        type: "dateTime",
        valueFormatter: (params) => {
          return getShiftDate(params.value);
        },
        flex: 0.13,
      },
      {
        field: "endDate",
        headerName: "End Date",
        type: "dateTime",
        valueFormatter: (params) => {
          return getShiftDate(params.value);
        },
        flex: 0.13,
      },
      {
        field: "status",
        headerName: "Status",
        type: "string",
        flex: 0.15,
        renderCell: (params) => {
          const val = params.value;
          return <div>{val}</div>;
        },
        sortComparator: getSortedTempAssignmentsByStatus,
      },
      {
        field: "baseSchedule",
        headerName: "Base Schedule",
        flex: 0.15,
        renderCell: (params) => {
          const row = params?.row?.baseSchedule;
          return params.row.status !== STATUS_CANCELED ? (
            <div>
              {isBaseScheduleAdded(row) ? (
                <Chip
                  className={classes.baseScheduleDoneChip}
                  label={TEMP_ASSIGNMENT_BASE_SCHEDULE_DONE_MESSAGE}
                ></Chip>
              ) : (
                <Chip
                  className={classes.baseScheduleMissingChip}
                  label={TEMP_ASSIGNMENT_BASE_SCHEDULE_MISSING_MESSAGE}
                ></Chip>
              )}
            </div>
          ) : (
            ""
          );
        },
        sortComparator: getSortedTempAssignmentsByBaseScheduleAdded,
      },
      {
        field: "action",
        headerName: ACTIONS,
        type: "string",
        flex: 0.15,
        sortable: false,
        renderCell: (params) => {
          return renderActionsCell(params.row);
        },
      },
      {
        field: "referenceNumber",
        headerName: "Reference #",
        valueGetter: (params) => {
          return params.row.referenceNumber;
        },
        type: "string",
        hide: true,
      },
      {
        field: "employeeName",
        headerName: "EE Name",
        valueGetter: (params) => {
          return `${params.row.empLastName}, ${params.row.empFirstName}`;
        },
        type: "string",
        hide: true,
      },
      {
        field: "contact",
        headerName: "EE Contact #",
        valueGetter: (params) => {
          return params.row.contactNumber;
        },
        type: "string",
        hide: true,
      },
      {
        field: "employeeID",
        headerName: "EE ID",
        valueGetter: (params) => {
          return params.row.eid;
        },
        type: "string",
        hide: true,
      },
      {
        field: "assignmentStartDate",
        headerName: "Assignment Start Date",
        valueGetter: (params) => {
          return params.row.searchFormat.startDate;
        },
        type: "string",
        hide: true,
      },
      {
        field: "assignmentEndDate",
        headerName: "Assignment End Date",
        valueGetter: (params) => {
          return params.row.searchFormat.endDate;
        },
        type: "string",
        hide: true,
      },
      {
        field: "effectiveStartDate",
        headerName: "Stars Effective Start Date",
        valueGetter: (params) => {
          return params.row.effectiveStartDate
            ? convert_Timestamp_To_Luxon_no_local_time(params.row.effectiveStartDate, params.row.tempStation).toFormat(
                "LL/dd/yyyy T ZZZZ"
              )
            : "";
        },
        type: "string",
        hide: true,
      },
      {
        field: "effectiveEndDate",
        headerName: "Stars Effective End Date",
        valueGetter: (params) => {
          return params.row.effectiveEndDate
            ? convert_Timestamp_To_Luxon_no_local_time(params.row.effectiveEndDate, params.row.tempStation).toFormat(
                "LL/dd/yyyy T ZZZZ"
              )
            : "";
        },
        type: "string",
        hide: true,
      },
      {
        field: "assignmentDateDonotMatch",
        headerName: "Assignment Dates Do Not Match STARS Effective Dates",
        valueGetter: (params) => {
          return params.row.effectiveStartDate &&
            params.row.searchFormat.startDate !==
              getShiftDate(convert_Timestamp_To_Luxon(params.row.effectiveStartDate, props.station))
            ? "YES"
            : params.row.effectiveEndDate &&
              params.row.searchFormat.endDate !==
                getShiftDate(convert_Timestamp_To_Luxon(params.row.effectiveEndDate, props.station))
            ? "YES"
            : "";
        },
        type: "string",
        hide: true,
      },
      {
        field: "sun",
        headerName: "SUN",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.SUN;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "mon",
        headerName: "MON",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.MON;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "tue",
        headerName: "TUE",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.TUE;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "wed",
        headerName: "WED",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.WED;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "thu",
        headerName: "THU",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.THU;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "fri",
        headerName: "FRI",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.FRI;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "sat",
        headerName: "SAT",
        valueGetter: (params) => {
          const baseSchedule = params.row.baseSchedule;
          const day = baseSchedule.SAT;
          return renderBaseScheduleTime(baseSchedule, day);
        },
        type: "string",
        hide: true,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        valueGetter: (params) => {
          return `${params.row.createdByLastName}, ${params.row.createdByFirstName} (${params.row.createdByEid})`;
        },
        type: "string",
        hide: true,
      },

      {
        field: "createDate",
        headerName: "Create Date",
        flex: 0.2,
        valueGetter: (params) => {
          return params.row.createdDate
            ? convert_Timestamp_To_Luxon_no_local_time(params.row.createdDate, params.row.tempStation).toFormat(
                "LL/dd/yyyy T ZZZZ"
              )
            : "";
        },
        hide: true,
        type: "string",
      },
    ];

    return (
      <div style={{ width: "100%", height: "inherit" }}>
        <DataGridPro
          apiRef={props.tableDesktopRef}
          rows={props.tempAssignmentList}
          columns={columns}
          getRowId={(tempAssignmentSnapshot) => tempAssignmentSnapshot.referenceNumber}
          sortModel={tempAssignmentData.sortModel}
          onSortModelChange={(event) => {
            dispatch(updateSortModel(event));
          }}
          sortingOrder={["asc", "desc"]}
          disableColumnMenu={true}
          checkboxSelection={true}
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
            setSelectionModel([params.id]);
          }}
          onRowClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          hideFooterPagination={true}
          rowHeight={90}
          components={{
            LoadingOverlay: CustomLoadingTableOverlay,
            NoRowsOverlay: CustomNoRowsTableOverlay,
            Toolbar: TemporaryAssignmentFormToolbar,
            ErrorOverlay: CustomErrorTableOverlay,
          }}
          componentsProps={{
            noRowsOverlay: { pageType: TEMPORARY_ASSIGNMENT_PAGE_TYPE },
            errorOverlay: {
              errorMessage: props.errorObject.message,
              errorCode: props.errorObject.code,
            },
            toolbar: { station: props.station },
          }}
          loading={props.isLoading}
          localeText={{
            toolbarExport: "Export",
            toolbarExportCSV: generateExportCsvButtonText(selectionModel, props.tempAssignmentList),
          }}
          error={props.isError ? true : undefined}
        />
        {isDialogOpen && (
          <WarningDialog
            isDialogOpen={isDialogOpen}
            header="Warning"
            message={dialogMessage}
            handleClickDialogClose={() => handleClickDialogClose()}
            severity={"error"}
          />
        )}

        {isCancelWarningOpen && (
          <ConfirmationDialog
            commentCharLimit={FREEFORM_REASON_CHAR_LIMIT_FOR_TEMP_ASSGNMENT}
            isDialogOpen={isCancelWarningOpen}
            title={CANCEL_TEMPORARY_ASSIGNMENT_TITLE}
            message={TEMP_ASSIGNMENT_CANCEL_MESSAGE(cancelEmpFirstName, cancelEmpLastName, cancelEmployeeId)}
            handleNoButtonClicked={() => handleCancelWarningDialogClose()}
            handleYesButtonClicked={handleCancelYesButtonClicked}
            hasComments={true}
            comments={comments}
            setComments={setComments}
            commentsError={commentsError}
            isYesLoading={isLoading}
            commentErrorMessage={commentErrorMessage}
            hasWarning={isActiveTempAssignment}
            hasInfo={true}
            infoMessage={TEMP_ASSIGNMENT_CANCEL_INFO_MESSAGE}
            warningMessage={TEMP_ASSIGNMENT_CANCEL_WARNING_MESSAGE}
            setCommentsError={setCommentsError}
            setCommentErrorMessage={setCommentErrorMessage}
          />
        )}
        {isCancelSuccessDialogOpen && (
          <SuccessDialog
            isSuccessDialogOpen={isCancelSuccessDialogOpen}
            title={TEMP_ASSIGNMENT_CONFIRMATION_TITLE}
            subTitle={REFERENCE_NUMBER_SUB_TITLE(tempAssignmentActionData.id)}
            message="Temporary Assignment Successfully Canceled"
            handleOKClick={() => setIsCancelSuccessDialogOpen(false)}
            hasError={!!cancelErrors}
            error={cancelErrors}
            redirectTo={props?.featureConfig?.adminPath}
          />
        )}
        {isSelfCancelWarningDialogOpen && (
          <WarningDialog
            isDialogOpen={isSelfCancelWarningDialogOpen}
            handleClickDialogClose={() => setIsSelfCancelWarningDialogOpen(false)}
            header={TEMP_ASSIGNMENT_ACTION_ERROR_TITLE}
            message={TEMP_ASSIGNMENT_SELF_ACTION_CANCEL_WARNING_MESSAGE}
            severity={"error"}
          />
        )}
        {isSelfEditWarningDialogOpen && (
          <WarningDialog
            isDialogOpen={isSelfEditWarningDialogOpen}
            handleClickDialogClose={() => setIsSelfEditWarningDialogOpen(false)}
            header={TEMP_ASSIGNMENT_ACTION_ERROR_TITLE}
            message={TEMP_ASSIGNMENT_SELF_ACTION_EDIT_WARNING_MESSAGE}
            severity={"error"}
          />
        )}
      </div>
    );
  };

  return <div className={classes.table}>{renderDesktopTempAssignments()}</div>;
};

TemporaryAssignmentTable.propTypes = {
  station: PropTypes.string.isRequired,
  tempAssignmentList: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  tableDesktopRef: PropTypes.object.isRequired,
  errorObject: PropTypes.object.isRequired,
};

export default TemporaryAssignmentTable;
